/**
 * TestimonialCard — V2.0 Foundation primitive
 *
 * A card for social proof: quote + optional headshot + optional milestone
 * metric + attribution.
 *
 * Example:
 *   <TestimonialCard
 *     photoUrl="/testimonials/sarah_m.jpg"
 *     quote="Informed rebuilt my credit story so investors would say yes."
 *     metric={{ value: "+94 pts", label: "credit score" }}
 *     name="Sarah M."
 *     role="Business owner · Atlanta, GA"
 *     featured
 *   />
 *
 * Test IDs:
 *   v2-testimonial-card, v2-testimonial-quote, v2-testimonial-metric,
 *   v2-testimonial-name, v2-testimonial-role, v2-testimonial-photo
 */
import React from "react";
import clsx from "clsx";
import { Quote } from "lucide-react";

export default function TestimonialCard({
  photoUrl,
  quote,
  metric,
  name,
  role,
  featured = false,
}) {
  return (
    <figure
      data-testid="v2-testimonial-card"
      className={clsx(
        "rounded-v2-2xl bg-v2-surface border border-v2-border shadow-v2-card p-6 md:p-8 flex flex-col gap-6",
        featured && "md:p-10 border-v2-borderStrong shadow-v2-lifted"
      )}
    >
      <Quote className={clsx("text-v2-accent flex-shrink-0", featured ? "h-8 w-8" : "h-6 w-6")} aria-hidden="true" />
      <blockquote
        data-testid="v2-testimonial-quote"
        className={clsx(
          "text-v2-text leading-relaxed",
          featured ? "text-v2-heading-md" : "text-v2-body-lg"
        )}
        style={{ fontFamily: "'Space Grotesk', 'Inter', sans-serif" }}
      >
        &ldquo;{quote}&rdquo;
      </blockquote>

      {metric ? (
        <div
          data-testid="v2-testimonial-metric"
          className="inline-flex items-baseline gap-2 rounded-v2-md bg-v2-successSoft px-3 py-2 self-start"
        >
          <span className="text-v2-heading-md font-bold text-v2-success">{metric.value}</span>
          <span className="text-v2-caption uppercase text-v2-textMuted">{metric.label}</span>
        </div>
      ) : null}

      <figcaption className="mt-auto flex items-center gap-3">
        {photoUrl ? (
          <img
            data-testid="v2-testimonial-photo"
            src={photoUrl}
            alt=""
            className="h-11 w-11 rounded-full object-cover border border-v2-border"
          />
        ) : (
          <div
            aria-hidden="true"
            data-testid="v2-testimonial-initials"
            className="h-12 w-12 rounded-full bg-v2-accentSoft text-v2-accentStrong grid place-items-center text-v2-body-md font-semibold"
          >
            {(() => {
              // V2.0 Phase 1 — derive first-name initial + last-initial from
              // the `name` prop ("Sarah M." → "SM", "Priya D." → "PD"). If a
              // single-word name is passed, fall back to the first character.
              const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
              if (parts.length === 0) return "?";
              const first = parts[0]?.[0] || "";
              const last = parts.length > 1 ? parts[parts.length - 1]?.[0] || "" : "";
              return (first + last).toUpperCase() || "?";
            })()}
          </div>
        )}
        <div className="flex flex-col">
          <span data-testid="v2-testimonial-name" className="text-v2-body-md font-semibold text-v2-text">
            {name}
          </span>
          {role ? (
            <span data-testid="v2-testimonial-role" className="text-v2-body-sm text-v2-textMuted">
              {role}
            </span>
          ) : null}
        </div>
      </figcaption>
    </figure>
  );
}
